// src/pages/workflow/WorkflowCreatePage.tsx
// Create a new pipeline: blank, from template, or import YAML.

import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';

import {
  Button,
  Input,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  ErrorAlert,
} from '@react/components';
import { TagPicker } from '@/components/TagPicker';
import { Select } from '@/components/Select';
import { TitleInput, AutoGrowTextarea } from '@/components/form';
import { assignTagsToEntity } from '@/features/task/logic';
import type { Tag } from '@core/types/tag';
import {
  createModeOptions,
  triggerTypeCards,
  errorHandlingCards,
  validatePipelineForm,
  validateYamlImport,
  buildCreatePipelinePayload,
  filterTemplates,
  getTemplateCategories,
  getEntityId,
  type CreateMode,
} from '@/features/workflow/logic';

// --- Icons ---

function VerifiedIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 12 15 16 9" fill="none" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

// --- Main Component ---

export const WorkflowCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useWorkflow();
  const { t } = useI18n();
  // State
  const [mode, setMode] = useState<CreateMode>('blank');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'TRIGGER_TYPE_MANUAL',
    error_handling: 'ERROR_HANDLING_STOP_ON_ERROR',
    category: '',
  });

  // Template state
  const [templates, setTemplates] = useState<any[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategory, setTemplateCategory] = useState('all');
  const [installingId, setInstallingId] = useState<string | null>(null);

  // YAML import state
  const [yamlContent, setYamlContent] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  // Load templates when switching to template mode
  useEffect(() => {
    if (mode !== 'template') return;
    setTemplatesLoading(true);
    store.ListTemplates?.()
      ?.then((res: any) => setTemplates(Array.isArray(res?.items) ? res.items : Array.isArray(store.items) ? store.items : []))
      ?.catch(() => {})
      ?.finally(() => setTemplatesLoading(false));
  }, [mode]);

  const filteredTemplates = useMemo(
    () => filterTemplates(templates, templateSearch, templateCategory),
    [templates, templateSearch, templateCategory],
  );

  const categories = useMemo(
    () => getTemplateCategories(templates),
    [templates],
  );

  // Handlers
  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validatePipelineForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildCreatePipelinePayload(formData);
      const result = await store.CreatePipeline(payload);
      const id = getEntityId(result.pipeline);

      // Assign tags via TagService after pipeline creation
      if (id && selectedTags.length > 0) {
        await assignTagsToEntity(selectedTags, id, 'workflow');
      }

      navigate(id ? `/workflows/${id}/edit` : '/workflows');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInstallTemplate = async (templateId: string) => {
    setInstallingId(templateId);
    try {
      const result = await store.InstallTemplate?.(templateId, {});
      const pipelineId = getEntityId(result?.pipeline);
      if (pipelineId) {
        navigate(`/workflows/${pipelineId}/edit`);
      }
    } catch {
      // Error handled by store
    } finally {
      setInstallingId(null);
    }
  };

  const handleImportYaml = async () => {
    const validationErrors = validateYamlImport(yamlContent);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await store.ImportPipelineYAML?.({ yaml_content: yamlContent });
      const id = getEntityId(result?.pipeline);
      navigate(id ? `/workflows/${id}/edit` : '/workflows');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.yml') || file.name.endsWith('.yaml') || file.name.endsWith('.json'))) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setYamlContent(ev.target?.result as string || '');
      };
      reader.readAsText(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setYamlContent(ev.target?.result as string || '');
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl pb-8">
      {/* Breadcrumb + Header */}
      <div>
        <nav className="text-sm mb-4">
          <Link to="/workflows" className="text-muted-foreground hover:text-foreground transition-colors">
            {t('workflow.pipelines')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('workflow.createPipeline')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('workflow.createPipeline')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('workflow.createPipelineDesc')}</p>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Step 1: Choose creation mode */}
      <div>
        <h2 className="text-sm font-medium text-foreground mb-3">{t('workflow.chooseMethod')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {createModeOptions.map((opt) => (
            <button
              key={opt.mode}
              type="button"
              onClick={() => setMode(opt.mode)}
              className={`
                flex flex-col items-center gap-3 p-5 rounded-lg border transition-all text-center
                ${mode === opt.mode
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50'
                }
              `}
            >
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center
                ${mode === opt.mode ? 'bg-primary/10' : 'bg-muted'}
              `}>
                <svg
                  className={`h-6 w-6 ${mode === opt.mode ? 'text-primary' : 'text-muted-foreground'}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d={opt.iconPath} />
                </svg>
              </div>
              <div>
                <p className={`text-sm font-medium ${mode === opt.mode ? 'text-primary' : 'text-foreground'}`}>
                  {t(opt.labelKey)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{t(opt.descKey)}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Mode: Blank — composer: ghost title/description, then property rail */}
      {mode === 'blank' && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-lg border border-border p-4">
            <TitleInput
              value={formData.name}
              onChange={(value) => handleChange('name', value)}
              placeholder={t('workflow.field.namePlaceholder')}
              initialFocus
              onEnter={() => document.getElementById('workflow-create-description')?.focus()}
              aria-label={t('workflow.field.name')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-destructive">
                {t(errors.name, { field: t('workflow.field.name'), min: 2, max: 200 })}
              </p>
            )}
            <AutoGrowTextarea
              id="workflow-create-description"
              value={formData.description}
              onChange={(value) => handleChange('description', value)}
              placeholder={t('workflow.field.descriptionPlaceholder')}
              minRows={1}
              className="mt-1"
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Input
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                placeholder={t('workflow.field.categoryPlaceholder')}
                aria-label={t('workflow.field.category')}
                className="max-w-[12rem]"
              />
              <TagPicker
                value={selectedTags}
                onChange={setSelectedTags}
                placeholder={t('tag.selectTags')}
              />
            </div>
          </div>

          {/* Trigger type — visual cards */}
          <Card>
            <CardHeader>
              <CardTitle>{t('workflow.triggerBehavior')}</CardTitle>
              <CardDescription>{t('workflow.triggerBehaviorDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  {t('workflow.field.triggerType')}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {triggerTypeCards.map((trigger) => (
                    <button
                      key={trigger.value}
                      type="button"
                      onClick={() => handleChange('trigger_type', trigger.value)}
                      className={`
                        flex flex-col items-start gap-2 p-3 rounded-lg border transition-all text-left
                        ${formData.trigger_type === trigger.value
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50'
                        }
                      `}
                    >
                      <svg
                        className={`h-5 w-5 flex-shrink-0 ${formData.trigger_type === trigger.value ? 'text-primary' : 'text-muted-foreground'}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d={trigger.iconPath} />
                      </svg>
                      <div>
                        <p className={`text-sm font-medium ${formData.trigger_type === trigger.value ? 'text-primary' : 'text-foreground'}`}>
                          {t(trigger.labelKey)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {t(trigger.descKey)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Error handling — compact cards */}
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  {t('workflow.field.errorHandling')}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {errorHandlingCards.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleChange('error_handling', opt.value)}
                      className={`
                        flex items-start gap-3 p-3 rounded-lg border transition-all text-left
                        ${formData.error_handling === opt.value
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50'
                        }
                      `}
                    >
                      <svg
                        className={`h-5 w-5 flex-shrink-0 mt-0.5 ${formData.error_handling === opt.value ? 'text-primary' : 'text-muted-foreground'}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d={opt.iconPath} />
                      </svg>
                      <div>
                        <p className={`text-sm font-medium ${formData.error_handling === opt.value ? 'text-primary' : 'text-foreground'}`}>
                          {t(opt.labelKey)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t(opt.descKey)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Link to="/workflows">
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              isLoading={isSubmitting}
            >
              {t('workflow.createAndEdit')}
            </Button>
          </div>
        </form>
      )}

      {/* Mode: Template — gallery */}
      {mode === 'template' && (
        <div className="space-y-4">
          {/* Search + Filter */}
          <div className="flex gap-3">
            <Input
              placeholder={t('workflow.searchTemplates')}
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              className="flex-1"
            />
            {categories.length > 0 && (
              <Select
                value={templateCategory}
                onChange={(e) => setTemplateCategory(e.target.value)} fullWidth={false} aria-label={t('workflow.filterCategory')}>
                <option value="all">{t('workflow.allCategories')}</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Select>
            )}
          </div>

          {/* Template grid */}
          {templatesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-5">
                    <div className="animate-pulse space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-muted rounded-lg" />
                        <div className="flex-1">
                          <div className="h-4 bg-muted rounded w-3/4" />
                          <div className="h-3 bg-muted rounded w-1/2 mt-2" />
                        </div>
                      </div>
                      <div className="h-3 bg-muted rounded w-full" />
                      <div className="h-3 bg-muted rounded w-4/5" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredTemplates.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <TemplateIcon className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-1">{t('workflow.noTemplates')}</h3>
                <p className="text-sm text-muted-foreground">{t('workflow.noTemplatesDesc')}</p>
                <Button
                  variant="secondary"
                  className="mt-4"
                  onClick={() => setMode('blank')}
                >
                  {t('workflow.startFromBlank')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((tmpl: any) => {
                const tmplId = getEntityId(tmpl);
                const isInstalling = installingId === tmplId;
                return (
                  <Card key={tmplId} hoverable className="h-full">
                    <CardContent className="p-5 flex flex-col h-full">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {tmpl.icon_url ? (
                            <img src={tmpl.icon_url} alt="" className="w-6 h-6" />
                          ) : (
                            <TemplateIcon className="w-5 h-5 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-semibold text-foreground truncate">{tmpl.name}</h3>
                            {tmpl.is_verified && (
                              <VerifiedIcon className="h-4 w-4 text-primary flex-shrink-0" />
                            )}
                          </div>
                          {tmpl.tagline && (
                            <p className="text-xs text-muted-foreground">{tmpl.tagline}</p>
                          )}
                        </div>
                      </div>

                      {tmpl.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3 flex-1">
                          {tmpl.description}
                        </p>
                      )}

                      {tmpl.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {tmpl.tags.slice(0, 3).map((tag: string) => (
                            <span key={tag} className="px-1.5 py-0.5 text-xs rounded bg-muted text-muted-foreground">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-3 border-t border-border mt-auto">
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <DownloadIcon className="h-3 w-3" />
                          {tmpl.install_count || 0}
                        </span>
                        <Button
                          size="sm"
                          onClick={() => handleInstallTemplate(tmplId)}
                          disabled={isInstalling}
                          isLoading={isInstalling}
                        >
                          {t('workflow.useTemplate')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Browse all templates link */}
          <div className="text-center pt-2">
            <Link to="/workflows/templates" className="text-sm text-primary hover:underline">
              {t('workflow.browseAllTemplates')}
            </Link>
          </div>
        </div>
      )}

      {/* Mode: Import YAML */}
      {mode === 'import' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('workflow.importYaml')}</CardTitle>
              <CardDescription>{t('workflow.importYamlDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Drag-drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleFileDrop}
                className={`
                  relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
                  ${isDragOver
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                  }
                `}
              >
                <UploadIcon className={`h-10 w-10 mx-auto mb-3 ${isDragOver ? 'text-primary' : 'text-muted-foreground/50'}`} />
                <p className="text-sm text-foreground font-medium">{t('workflow.dropFileHere')}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('workflow.dropFileFormats')}</p>
                <label className="mt-3 inline-block">
                  <span className="text-sm text-primary hover:underline cursor-pointer">{t('workflow.browseFile')}</span>
                  <input
                    type="file"
                    accept=".yml,.yaml,.json"
                    className="sr-only"
                    onChange={handleFileSelect}
                  />
                </label>
              </div>

              {/* YAML textarea */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  {t('workflow.pasteYaml')}
                </label>
                <textarea
                  value={yamlContent}
                  onChange={(e) => {
                    setYamlContent(e.target.value);
                    if (errors.yaml) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.yaml;
                        return next;
                      });
                    }
                  }}
                  placeholder={t('workflow.yamlPlaceholder')}
                  rows={12}
                  className={`
                    w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground
                    placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring
                    font-mono resize-y
                    ${errors.yaml ? 'border-destructive' : 'border-input'}
                  `}
                />
                {errors.yaml && (
                  <p className="text-sm text-destructive mt-1">{t(errors.yaml)}</p>
                )}
              </div>

              {yamlContent && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Badge variant="success" size="sm">{t('workflow.ready')}</Badge>
                  <span>{yamlContent.split('\n').length} {t('workflow.lines')}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Link to="/workflows">
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button
              onClick={handleImportYaml}
              disabled={isSubmitting || !yamlContent.trim()}
              isLoading={isSubmitting}
            >
              {t('workflow.importAndEdit')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

export default WorkflowCreatePage;
